import { Link } from "@tanstack/react-router";
import { Mail, Phone, MapPin, Smartphone, Monitor, ShieldCheck } from "lucide-react";

export function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative overflow-hidden">
      <div className="absolute inset-0 bg-linear-to-br from-blue-900 via-blue-800 to-indigo-900" />
      <div className="absolute inset-0 opacity-10">
        <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" className="h-full w-full">
          <path
            fill="currentColor"
            d="M44.7,-76.4C58.8,-69.2,71.8,-59.1,81.6,-46.6C91.4,-34.1,98.1,-19.2,95.8,-5.3C93.5,8.6,82.2,21.5,71.6,32.4C61,43.3,51.1,52.2,40,59.2C28.9,66.2,16.6,71.3,3.6,69.8C-9.4,68.3,-21.2,60.1,-32.8,52.1C-44.4,44.1,-55.8,36.3,-65.8,26.2C-75.8,16.1,-84.4,3.7,-82.8,-7.2C-81.2,-18.1,-69.4,-27.5,-58.1,-35.9C-46.8,-44.3,-36,-51.7,-24.3,-57.6C-12.6,-63.5,-0.1,-67.9,13.4,-71.5C26.9,-75.1,30.6,-83.6,44.7,-76.4Z"
            transform="translate(100 100)"
          />
        </svg>
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
        <div className="mb-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="sm:col-span-1">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/10 backdrop-blur-sm shadow-lg">
                <img
                  src="/patataskslogo.png"
                  alt="PataTasks Logo"
                  className="h-10 w-10 rounded-lg object-contain"
                />
              </div>
              <div>
                <h3 className="font-bold text-white">PataTasks</h3>
                <p className="text-[10px] font-semibold text-white uppercase tracking-wider">
                  App & Web Testing
                </p>
              </div>

              <div>
                <h4 className="mb-4 font-semibold text-white flex items-center gap-2">
                  <span className="h-1 w-6 rounded-full bg-amber-400" />
                  Opportunities
                </h4>
                <nav className="space-y-2.5">
                  <a
                    href="mailto:support@patatasks.app"
                    className="flex items-center gap-2 text-sm font-medium text-blue-100 transition-all duration-200 hover:translate-x-1 hover:text-white"
                  >
                    <Mail className="h-4 w-4" />
                    Support
                  </a>
                </nav>
              </div>
            </div>
            <p className="text-sm leading-relaxed text-blue-100">
              Test mobile apps and web apps. Get rewarded for sharing real feedback on apps and
              websites.
            </p>
          </div>
          <div>
            <h4 className="mb-4 font-semibold text-white flex items-center gap-2">
              <span className="h-1 w-6 rounded-full bg-blue-400" />
              Explore
            </h4>
            <nav className="space-y-2.5">
              {[
                { to: "/app-tests", label: "App Tests" },
                { to: "/webapp-tests", label: "Web App Tests" },
              ].map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="flex items-center gap-2 text-sm font-medium text-blue-100 transition-all duration-200 hover:translate-x-1 hover:text-white"
                >
                  {link.to === "/app-tests" ? (
                    <Smartphone className="h-4 w-4" />
                  ) : (
                    <Monitor className="h-4 w-4" />
                  )}
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          <div>
            <h4 className="mb-4 font-semibold text-white flex items-center gap-2">
              <span className="h-1 w-6 rounded-full bg-emerald-400" />
              Account
            </h4>
            <nav className="space-y-2.5">
              {[
                { to: "/signup", label: "Sign Up" },
                { to: "/login", label: "Login" },
                { to: "/profile", label: "Profile" },
                { to: "/wallet", label: "Wallet" },
              ].map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="block text-sm font-medium text-blue-100 transition-all duration-200 hover:translate-x-1 hover:text-white"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          <div>
            <h4 className="mb-4 font-semibold text-white flex items-center gap-2">
              <span className="h-1 w-6 rounded-full bg-amber-400" />
              Legal
            </h4>
            <nav className="space-y-2.5">
              {[
                { to: "/about", label: "About" },
                { to: "/contact", label: "Contact" },
                { to: "/faq", label: "FAQ" },
                { to: "/terms", label: "Terms of Service" },
                { to: "/privacy", label: "Privacy Policy" },
              ].map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="block text-sm font-medium text-blue-100 transition-all duration-200 hover:translate-x-1 hover:text-white"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>
        </div>

        <div className="border-t border-white/10" />

        <div className="mt-6 flex flex-col items-center justify-center gap-4 text-sm">
          <div className="flex items-center gap-2 text-emerald-300">
            <ShieldCheck className="h-4 w-4" />
            <span className="font-medium">ISO 27001 Verified</span>
          </div>
          <p className="text-blue-200">&copy; {currentYear} PataTasks. All rights reserved.</p>
          <div className="flex items-center gap-4 text-blue-200">
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
              Live Tests
            </span>
            <span className="hidden md:inline text-white/20">|</span>
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-blue-400" />
              Secure Payments
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
